import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Layout } from "../components/styled";
import Footer from "../components/footer";
import Header from "../components/header";
import useOrderLookup, { useOrderRefresh } from "../hooks/useOrderLookup";
import { Skeleton, Card, Statistic, Steps, notification } from "antd";
import ErrorComponent from "../components/error";
import LocationCard from "../components/location/card";
import useLocationLookup from "../hooks/useLocationLookup";
import ListItems from "../components/item/line-items";
import { calculatePrettyTotals } from "../helpers";
import { TotalLines } from "../components/cost/total";
import {
  Enum_Componentorderfulfillment_State,
  Enum_Order_State,
} from "../queries/auto-generated/schemas";
import TimeScheduler from "../components/time-scheduler";
import { useUpdatePickupTimeMutation } from "../queries/auto-generated/hooks";
import _ from "lodash";
import CancelOrder from "../components/cancel-order";
import { OnSetTimeFunc } from "../types";

const { Step } = Steps;

const OrderPage: FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { order, loading, builtItems, missingItems, missingMods } =
    useOrderLookup(orderId);
  const { location, loading: locationLookupLoading } = useLocationLookup(
    order?.location_id
  );
  const [updateOrderPickupTime] = useUpdatePickupTimeMutation();

  const displayError = (message?: string) => {
    // TODO: Track error
    notification.error({
      message: message
        ? message
        : "Trouble updating pickup time. If the issue persists, please consider canceling your order and creating a new one.",
      placement: "bottomRight",
    });
  };

  const handleUpdatePickupTime: OnSetTimeFunc = (time) => {
    return new Promise((resolve, reject) => {
      if (!!time && order) {
        updateOrderPickupTime({
          variables: {
            jooOrderId: order.id,
            requestedPickupTime: time,
          },
        })
          .then((response) => {
            const error = _.get(response, "data.updatePickupTime.errors[0]");
            if (error) {
              displayError(error.detail);
              reject(error);
            } else {
              resolve();
            }
          })
          .catch((e) => {
            console.error(e);
            displayError();
            reject(e);
          });
      } else {
        reject(new Error("Missing time or order"));
      }
    });
  };

  const { refresher } = useOrderRefresh();

  const orderNumber = order?.receipt_number ?? order?.id ?? "";

  const titleContent = loading
    ? "Order"
    : order
    ? `Order #${orderNumber.toUpperCase()}`
    : "Order not found : (";

  const errorMessage =
    missingItems || missingMods
      ? `*The menu has been updated and some of the items or item details may having missing info. 
          However, the total price is still accurate.`
      : undefined;

  const { prettyTotal, prettySubtotal, prettyTaxes, prettyTip } =
    calculatePrettyTotals(
      order?.total_money?.amount,
      order?.total_tax_money?.amount,
      order?.total_tip_money?.amount
    );

  const fulfillment = order?.fulfillments?.[0];
  const fulfillmentStateKey = fulfillment?.state;
  const orderStateKey = order?.state;

  const currentFulfillmentStateIndex = fulfillmentStates.findIndex(
    (state) => state.state === fulfillmentStateKey
  );

  // Check if fulfillment was canceled or failed
  let badFulfillment = undefined;
  const fulfillmentInfo = fulfillmentStates[currentFulfillmentStateIndex];
  if (
    !!fulfillmentInfo &&
    (fulfillmentInfo.state === Enum_Componentorderfulfillment_State.Canceled ||
      fulfillmentInfo.state === Enum_Componentorderfulfillment_State.Failed)
  ) {
    badFulfillment = fulfillmentInfo;
  }

  const orderState = orderStates.find((state) => state.state === orderStateKey);

  const orderEditable =
    orderStateKey === Enum_Order_State.Open &&
    fulfillmentStateKey === Enum_Componentorderfulfillment_State.Proposed;

  useEffect(() => {
    if (orderState?.endState ?? true) {
      return;
    } else {
      refresher(order?.id);
      const intervalId = setInterval(() => {
        refresher(order?.id);
      }, 1000 * 120);
      return () => clearInterval(intervalId);
    }
  }, [refresher, orderState, order]);

  const requestedPickupTime = fulfillment?.pickup_details?.pickup_at;

  /**
   * I referenced this guide on using matchMedia
   * @see https://bit.ly/35dXcIX
   */
  const maxMobilePixels = 600;
  const [isMobile, setIsMobile] = useState<boolean>(
    window.innerWidth <= maxMobilePixels
  );
  useEffect(() => {
    const query = matchMedia(`(max-width: ${maxMobilePixels}px)`);
    const listener = ({ matches }: { matches: boolean }) => {
      setIsMobile(matches);
    };
    query.addEventListener("change", listener);

    return () => query.removeEventListener("change", listener);
  }, []);

  return (
    <Layout>
      <Header floatContent={false}>
        <Title>{titleContent}</Title>
      </Header>
      <Content>
        <Skeleton active loading={loading}>
          {order ? (
            <>
              <Card
                actions={[
                  <StyledButton
                    type="link"
                    href={order?.receipt_url ?? ""}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Order receipt link
                  </StyledButton>,
                ]}
              >
                {badFulfillment ? (
                  <Statistic
                    title="Status"
                    value={badFulfillment?.label ?? "Error"}
                    style={{ textAlign: "center" }}
                  />
                ) : (
                  <>
                    <Statistic
                      title="Status"
                      value={orderState?.label ?? "Error"}
                      style={{ textAlign: "center" }}
                    />
                    <StyledSteps
                      isMobile={isMobile}
                      status={
                        orderState?.state === Enum_Order_State.Canceled
                          ? "error"
                          : undefined
                      }
                      current={currentFulfillmentStateIndex} // TODO: Show fulfillment error states
                      size="small"
                      progressDot={!isMobile}
                      direction={isMobile ? "vertical" : "horizontal"}
                    >
                      {fulfillmentStates
                        .filter((state) => state.displayStep)
                        .map((state, index) => (
                          <Step title={state.shortDescription} key={index} />
                        ))}
                    </StyledSteps>
                  </>
                )}
              </Card>
              {locationLookupLoading ? (
                <Card loading />
              ) : location ? (
                [
                  <LocationCard
                    key="location_card"
                    location={location}
                    showActionButton={false}
                  />,
                  requestedPickupTime && (
                    <TimeScheduler
                      key="time_scheduler"
                      requestedPickupTime={requestedPickupTime}
                      shopLocation={location}
                      onSetTime={handleUpdatePickupTime}
                      canEdit={orderEditable}
                      showCancelButton
                      showEditTop
                      forOrderPage
                    />
                  ),
                ]
              ) : (
                <ErrorComponent
                  description="Unable to find location"
                  error={"Unable to find location"}
                />
              )}
              <ListItems
                canEdit={false}
                lineItems={builtItems}
                orderNote={fulfillment?.pickup_details?.note ?? undefined}
                showTitle
                errorMessage={errorMessage}
              />
              <Card>
                <TotalLines
                  prettySubtotal={prettySubtotal}
                  prettyTaxes={prettyTaxes}
                  prettyTotal={prettyTotal}
                  prettyTip={prettyTip}
                />
              </Card>
              {orderEditable && <CancelOrder order={order} />}
            </>
          ) : (
            <ErrorComponent
              description={`Unable to find that order. 
                Please contact customer service if you believe this to be an error.`}
              error="Unable to locate order"
            />
          )}
        </Skeleton>
      </Content>
      <Footer />
    </Layout>
  );
};

const fulfillmentStates: FulfillmentStateData[] = [
  {
    state: Enum_Componentorderfulfillment_State.Proposed,
    label: "Accepted",
    endState: false,
    shortDescription: "Order received",
    displayStep: true,
  },
  {
    state: Enum_Componentorderfulfillment_State.Reserved,
    label: "Started",
    endState: false,
    shortDescription: "Preparing order",
    displayStep: true,
  },
  {
    state: Enum_Componentorderfulfillment_State.Prepared,
    label: "Ready",
    endState: false,
    shortDescription: "Ready for pickup",
    displayStep: true,
  },
  {
    state: Enum_Componentorderfulfillment_State.Completed,
    label: "Completed",
    endState: true,
    shortDescription: "Completed",
    displayStep: true,
  },
  {
    state: Enum_Componentorderfulfillment_State.Canceled,
    label: "Canceled",
    endState: true,
    shortDescription: "",
    displayStep: false,
  },
  {
    state: Enum_Componentorderfulfillment_State.Failed,
    label: "Failed",
    endState: true,
    shortDescription: "",
    displayStep: false,
  },
];

const orderStates: OrderStateData[] = [
  {
    state: Enum_Order_State.Open,
    label: "Open",
    endState: false,
  },
  {
    state: Enum_Order_State.Completed,
    label: "Completed",
    endState: true,
  },
  {
    state: Enum_Order_State.Canceled,
    label: "Canceled",
    endState: true,
  },
];

const Content = styled.main`
  padding: 1em;
  width: 600px;
  max-width: 100%;
  margin: auto;

  & > div {
    margin-bottom: 2em;
  }
`;

const Title = styled.h1`
  text-align: center;
`;

const StyledSteps = styled(Steps).withConfig({
  shouldForwardProp: (prop) => !["isMobile"].includes(prop),
})<{ isMobile: boolean }>`
  margin-top: 14px;
  ${(p) =>
    p.isMobile
      ? `align-items: center;
  .ant-steps-item {
    width: 163px;
  }`
      : ""}
`;

const StyledButton = styled.a`
  text-decoration: underline;
  text-decoration-color: #ffeb19;
`;

interface FulfillmentStateData {
  state: Enum_Componentorderfulfillment_State;
  label: string;
  endState: boolean;
  shortDescription: string;
  displayStep: boolean;
}

interface OrderStateData {
  state: Enum_Order_State;
  label: string;
  endState: boolean;
}

export default OrderPage;
